import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from '@/lib/router';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppAccessProvider, useAppAccess } from './contexts/AppAccessContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LandingPage } from './components/LandingPage';
import { Auth } from './pages/Auth';
import { AuthCallback } from './pages/AuthCallback';
import { ComingSoon } from './pages/ComingSoon';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Docs } from './pages/Docs';
import { NotFound } from './pages/NotFound';
import { runtimeRouteManifest, type RouteId } from './routes/manifest';
import { buildUnauthorizedAuthPath } from './lib/auth/recovery';

const ChatLayout = lazy(() =>
  import('./components/layout/ChatLayout').then((m) => ({ default: m.ChatLayout })),
);
const Account = lazy(() =>
  import('./pages/Account').then((m) => ({ default: m.Account })),
);
const ClaimChartsLayout = lazy(() =>
  import('./pages/ClaimCharts').then((m) => ({ default: m.ClaimChartsLayout })),
);
const TabularReviewsLayout = lazy(() =>
  import('./pages/TabularReviews').then((m) => ({ default: m.TabularReviewsLayout })),
);
const Workflows = lazy(() =>
  import('./pages/Workflows').then((m) => ({ default: m.Workflows })),
);
const MatterDetail = lazy(() =>
  import('./pages/MatterDetail').then((m) => ({ default: m.MatterDetail })),
);

// Code-split the research surface. ResearchSearch (corpus search + the
// legacy browser under a Browse tab) is the only page that's not part of
// the daily-use chassis (chat / matters / claim charts / tabular /
// workflows), so paying its bundle cost on every app load is wasted bytes
// for the common case. lazy() keeps it out of the main bundle until the
// user navigates to /research.
const ResearchSearch = lazy(() =>
  import('./pages/ResearchSearch').then((m) => ({
    default: m.ResearchSearch,
  })),
);

// Code-split the standalone document editor page for the same reason: it
// eagerly imports PatProDocxEditor and with it the whole @patpro/docx-core +
// @patpro/docx-react editor stack (~2.5 MB of main-bundle source). The
// in-chat docx_editor tab is lazy for the same reason (render-tab-content).
const DocumentEditor = lazy(() =>
  import('./pages/DocumentEditor').then((m) => ({
    default: m.DocumentEditor,
  })),
);
const UxLab = lazy(() =>
  import('./dev/ux-lab/UxLab').then((m) => ({ default: m.UxLab })),
);
const WordAddinLab = import.meta.env.DEV
  ? lazy(() =>
      import('./dev/word-addin-lab/WordAddinLab').then((m) => ({
        default: m.WordAddinLab,
      })),
    )
  : null;

function PageFallback() {
  return (
    <div
      className="flex justify-center items-center h-screen"
      role="status"
      aria-label="Loading page"
    >
      <div
        className="w-10 h-10 border-4 border-border border-t-navy-500 rounded-full animate-cell-spin"
        aria-hidden="true"
      />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

// Route-level boundary (SPEC_03): contains a crash to the current surface
// and auto-clears when the user navigates elsewhere, so a broken
// /document-editor never takes /chat down with it.
function RouteBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageFallback />;
  }

  if (!user) {
    return <Navigate to={buildUnauthorizedAuthPath(location)} replace />;
  }

  return <RouteBoundary>{children}</RouteBoundary>;
}

// Owner-only gate for the web app routes (CC-SPEC item 4). Authenticated but
// non-owner users get the "coming soon" view and are routed to connect in
// Claude. `/account` deliberately uses ProtectedRoute (auth only) so every
// confirmed user can reach their account + the Connect-in-Claude block.
function AppRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { appAccess, loading: accessLoading } = useAppAccess();
  const location = useLocation();

  if (authLoading || (user && accessLoading)) {
    return <PageFallback />;
  }

  if (!user) {
    return <Navigate to={buildUnauthorizedAuthPath(location)} replace />;
  }

  if (appAccess !== true) {
    return <ComingSoon />;
  }

  return <RouteBoundary>{children}</RouteBoundary>;
}

export function App() {
  const routeElements: Record<RouteId, ReactNode> = {
    landing: <LandingPage />,
    privacy: <Privacy />,
    terms: <Terms />,
    docs: <Docs />,
    chat: (
      <AppRoute>
        <ChatLayout />
      </AppRoute>
    ),
    'claim-charts': (
      <AppRoute>
        <ClaimChartsLayout />
      </AppRoute>
    ),
    'tabular-reviews': (
      <AppRoute>
        <TabularReviewsLayout />
      </AppRoute>
    ),
    workflows: (
      <AppRoute>
        <Workflows />
      </AppRoute>
    ),
    research: (
      <AppRoute>
        <ResearchSearch />
      </AppRoute>
    ),
    'document-editor': (
      <AppRoute>
        <DocumentEditor />
      </AppRoute>
    ),
    'matter-detail': (
      <AppRoute>
        <MatterDetail />
      </AppRoute>
    ),
    account: (
      <ProtectedRoute>
        <Account />
      </ProtectedRoute>
    ),
    auth: <Auth />,
    'auth-callback': <AuthCallback />,
    'ux-lab': import.meta.env.DEV ? <UxLab /> : <NotFound />,
    'word-addin-lab':
      import.meta.env.DEV && WordAddinLab ? <WordAddinLab /> : <NotFound />,
    'not-found': <NotFound />,
  };

  return (
    <ThemeProvider>
      <AuthProvider>
        <AppAccessProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  {runtimeRouteManifest.map((route) => (
                    <Route
                      key={route.id}
                      path={route.path}
                      element={routeElements[route.id]}
                    />
                  ))}
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </AppAccessProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
